import { DashboardWidget } from "models/workspace";

import { WidgetKeys } from "components/dashboard/constants/widgets.constants";
import { getWidgetName } from "components/dashboard/utils/getWidgetName";

export type DashboardRowListT = {
  key: string;
  title: string;
  type: string;
  widgets: {
    type: string;
    count: number;
    color: string;
    id: string;
  }[];
  actions: JSX.Element;
}[];

export const TAG_ORDER = [
  WidgetKeys.MapWidgetKey,
  WidgetKeys.ChartWidgetKey,
  WidgetKeys.MultiphaseChartWidgetKey,
  WidgetKeys.RigFracChartWidgetKey,
  WidgetKeys.WellStatusChartWidgetKey,
  WidgetKeys.DataTableWidgetKey,
  WidgetKeys.RigFracDataTableWidgetKey,
  WidgetKeys.XdaViewerWidgetKey,
  WidgetKeys.XdaWidgetKey,
  WidgetKeys.ThreeDWidgetKey,
  WidgetKeys.TypeWellEditorWidgetKey,
  WidgetKeys.TypeWellDetailsWidgetKey,
  WidgetKeys.WellCardWidgetKey,
  WidgetKeys.InfoWidgetKey,
  WidgetKeys.SurveyVisWidgetKey,
  WidgetKeys.CompletionWidgetKey,
  WidgetKeys.TypeLogWidgetKey,
  WidgetKeys.MidstreamChartWidgetKey,
  WidgetKeys.MidstreamDataTableWidgetKey,
  WidgetKeys.FacilityTicketWidgetKey,
  WidgetKeys.WellDataAnalyzer,
  WidgetKeys.ForecastingWidgetKey,
  WidgetKeys.FeatureImportanceWidgetKey,
  WidgetKeys.PadScenarioWidgetKey,
  WidgetKeys.GeoMapLoaderWidgetKey,
  WidgetKeys.SamWidgetKey,
  WidgetKeys.SamTableWidgetKey,
  WidgetKeys.SamRfPvWidgetKey,
  WidgetKeys.ExploitableReportWidgetKey,
  WidgetKeys.AutomationWidgetKey,
  "Midstream", // Placeholder for the submenu, to always be at the end
  "default"
];

export const MIDSTREAM_WIDGETS = [
  WidgetKeys.MidstreamChartWidgetKey,
  WidgetKeys.MidstreamDataTableWidgetKey,
  WidgetKeys.FacilityTicketWidgetKey
];

export const TYPE_ORDER = {
  Personal: 0,
  Organization: 1,
  System: 2
};

export const getWidgetTags = (
  widgets: DashboardWidget[] | null | undefined
): {
  type: string;
  count: number;
  color: string;
  id: string;
}[] => {
  if (!widgets) return [];

  const widgetCounts = widgets.reduce((acc, widget) => {
    const widgetName = getWidgetName(widget.type);
    acc[widgetName] = acc[widgetName] || { count: 0, originalType: widget.type };
    acc[widgetName].count += 1;
    return acc;
  }, {} as Record<string, { count: number; originalType: string }>);

  return Object.entries(widgetCounts)
    .sort(([, aData], [, bData]) => {
      const aIndex = TAG_ORDER.indexOf(aData.originalType);
      const bIndex = TAG_ORDER.indexOf(bData.originalType);

      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }

      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;

      return 0;
    })
    .map(([type, { count, originalType }]) => ({
      type,
      count,
      color: getWidgetColor(originalType),
      id: originalType
    }));
};

export const getAvailableWidgetTypes = (
  dashboardRowList: DashboardRowListT
): { id: string; type: string }[] => {
  const widgetMap = new Map<string, { id: string; type: string }>();

  dashboardRowList.forEach((record) =>
    record.widgets.forEach(({ id, type }) => {
      if (id?.trim() && type?.trim()) {
        widgetMap.set(id, { id, type });
      }
    })
  );

  return Array.from(widgetMap.values()).sort((a, b) => a.type.localeCompare(b.type));
};

export const getWidgetColor = (type: string): string => {
  const colors: Record<string, string> = {
    [WidgetKeys.ChartWidgetKey]: "darkorange",
    [WidgetKeys.CompletionWidgetKey]: "sienna",
    [WidgetKeys.DataTableWidgetKey]: "goldenrod",
    [WidgetKeys.ExploitableReportWidgetKey]: "darkkhaki",
    [WidgetKeys.FacilityTicketWidgetKey]: "darkslategray",
    [WidgetKeys.FeatureImportanceWidgetKey]: "darkseagreen",
    [WidgetKeys.ForecastingWidgetKey]: "peru",
    [WidgetKeys.GeoMapLoaderWidgetKey]: "darkturquoise",
    [WidgetKeys.InfoWidgetKey]: "indianred",
    [WidgetKeys.MapWidgetKey]: "forestgreen",
    [WidgetKeys.MidstreamChartWidgetKey]: "darkorchid",
    [WidgetKeys.MidstreamDataTableWidgetKey]: "saddlebrown",
    [WidgetKeys.MultiphaseChartWidgetKey]: "chocolate",
    [WidgetKeys.PadScenarioWidgetKey]: "tomato",
    [WidgetKeys.RigFracChartWidgetKey]: "olivedrab",
    [WidgetKeys.RigFracDataTableWidgetKey]: "cadetblue",
    [WidgetKeys.SamRfPvWidgetKey]: "mediumseagreen",
    [WidgetKeys.SamTableWidgetKey]: "royalblue",
    [WidgetKeys.SamWidgetKey]: "slateblue",
    [WidgetKeys.SurveyVisWidgetKey]: "mediumorchid",
    [WidgetKeys.ThreeDWidgetKey]: "teal",
    [WidgetKeys.TypeLogWidgetKey]: "seagreen",
    [WidgetKeys.TypeWellDetailsWidgetKey]: "darkcyan",
    [WidgetKeys.TypeWellEditorWidgetKey]: "dodgerblue",
    [WidgetKeys.WellCardWidgetKey]: "firebrick",
    [WidgetKeys.WellStatusChartWidgetKey]: "darkgoldenrod",
    [WidgetKeys.WellDataAnalyzer]: "crimson",
    [WidgetKeys.XdaViewerWidgetKey]: "darkslateblue",
    [WidgetKeys.XdaWidgetKey]: "steelblue",
    [WidgetKeys.AutomationWidgetKey]: "mediumpurple",
    default: "dimgray"
  };

  return colors[type.toLowerCase()] || colors.default;
};
